import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { toast } from "sonner";
import { UploadSimple, CircleNotch, Sparkle, FileText, ArrowsClockwise, DownloadSimple, MagicWand } from "@phosphor-icons/react";
import { ScoreInfo } from "@/components/mai/Explain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_COLOR = { strong: "#047857", partial: "#B45309", missing: "#B91C1C" };
const PRIORITY_COLOR = { Critical: "#B91C1C", High: "#B45309", Medium: "#1D4ED8" };
const STATUS_META = {
  drafted: { label: "Drafted", cls: "bg-emerald-700" },
  partial: { label: "Partial", cls: "bg-amber-600" },
  needs_input: { label: "Needs evidence", cls: "bg-red-700" },
};

const Block = ({ b }) => {
  if (b.type === "paragraph") return <p className="text-[13px] text-slate-700 leading-relaxed">{b.text}</p>;
  if (b.type === "kv") return (
    <table className="w-full text-[12px] border border-slate-200">
      <tbody>
        {b.items.map(([k, v], i) => (
          <tr key={i} className="border-b border-slate-100 last:border-0">
            <td className="px-3 py-1.5 font-semibold text-slate-700 bg-slate-50 w-52 align-top">{k}</td>
            <td className="px-3 py-1.5 text-slate-600">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border border-slate-200">
        <thead><tr className="bg-slate-100">
          {b.headers.map((h) => <th key={h} className="px-2.5 py-1.5 text-left font-semibold text-slate-700 border-b border-slate-200">{h}</th>)}
        </tr></thead>
        <tbody>
          {b.rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 align-top">
              {row.map((v, j) => <td key={j} className="px-2.5 py-1.5 text-slate-600">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function DraftTab({ productId, product }) {
  const [record, setRecord] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/gvd/${productId}/draft`).then((r) => { setRecord(r.data || null); setLoaded(true); });
  }, [productId]);

  useEffect(() => { setLoaded(false); setRecord(null); load(); }, [load]);

  const generate = async () => {
    setBusy("generate");
    try {
      const r = await axios.post(`${API}/gvd/${productId}/draft`);
      setRecord(r.data);
      toast.success(`Draft v${r.data.version} compiled from live platform data`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft generation failed");
    } finally { setBusy(null); }
  };

  const doExport = async (format) => {
    setBusy(format);
    try {
      const r = await axios.post(`${API}/gvd/${productId}/draft/export`, { format });
      toast.success("Export ready — downloading");
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.download_url}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setBusy(null); }
  };

  const draft = record?.draft;
  const c = record?.completeness;

  if (!loaded) return <p className="text-center text-xs text-slate-400 py-16">Loading…</p>;

  if (!draft) return (
    <div data-testid="draft-empty-state" className="bg-white border border-slate-200 px-8 py-16 text-center">
      <MagicWand size={32} className="mx-auto text-slate-300" />
      <p className="font-heading font-bold text-sm mt-4">No draft yet for {product?.name}</p>
      <p className="text-xs text-slate-500 mt-2 max-w-lg mx-auto leading-relaxed">
        The Auto-Draft Engine compiles a complete Global Value Dossier deterministically — every section is traced to your
        evidence inventory, country intelligence, gap analysis and GMARI™ readiness. No generative AI is used.
      </p>
      <button data-testid="generate-draft-btn" disabled={!!busy} onClick={generate}
        className="mt-6 bg-slate-900 text-white text-xs font-semibold px-5 py-3 hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50">
        {busy === "generate" ? <CircleNotch size={14} className="animate-spin" /> : <MagicWand size={14} weight="bold" />}
        Generate GVD Draft
      </button>
    </div>
  );

  return (
    <>
      <div data-testid="draft-completeness" className="bg-slate-900 text-white px-6 py-5 flex flex-wrap items-center gap-x-10 gap-y-3">
        <div>
          <p className="overline-label text-white/50">Draft Completeness <ScoreInfo scoreId="gvd_draft_completeness" light /></p>
          <p className="font-heading font-extrabold text-4xl mt-1">{c.score}<span className="text-base text-white/40">/100</span></p>
        </div>
        <div className="flex items-center gap-5 text-xs">
          <span className="text-emerald-300">{c.drafted} drafted</span>
          <span className="text-amber-300">{c.partial} partial</span>
          <span className="text-red-300">{c.needs_input} need evidence</span>
        </div>
        <p className="text-[11px] text-white/50">v{record.version} · {new Date(record.created_at).toLocaleString()} · by {record.created_by}</p>
        <div className="flex items-center gap-2 ml-auto">
          <button data-testid="regenerate-draft-btn" disabled={!!busy} onClick={generate}
            className="text-[11px] font-semibold px-3.5 py-2.5 border border-white/25 text-white hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "generate" ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />} Regenerate
          </button>
          <button data-testid="export-draft-pdf" disabled={!!busy} onClick={() => doExport("pdf")}
            className="text-[11px] font-semibold px-3.5 py-2.5 bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "pdf" ? <CircleNotch size={13} className="animate-spin" /> : <DownloadSimple size={13} />} PDF
          </button>
          <button data-testid="export-draft-docx" disabled={!!busy} onClick={() => doExport("docx")}
            className="text-[11px] font-semibold px-3.5 py-2.5 bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-1.5 disabled:opacity-50">
            {busy === "docx" ? <CircleNotch size={13} className="animate-spin" /> : <FileText size={13} />} Word
          </button>
        </div>
      </div>

      <div data-testid="draft-document" className="bg-white border border-slate-300 shadow-sm">
        <div className="px-10 py-10 text-center border-b-4" style={{ borderColor: "#0F2A3D" }}>
          <p className="overline-label text-slate-400">MAI Matrix™ · GVD Auto-Draft Engine · Deterministic compilation</p>
          <h2 className="font-heading font-extrabold text-2xl tracking-tight mt-3" style={{ color: "#0F2A3D" }}>{draft.title}</h2>
          <p className="text-base font-semibold text-slate-700 mt-2">{draft.product.name}</p>
          <p className="text-xs text-slate-500">{draft.product.indication}</p>
          <p className="font-heading font-bold text-emerald-700 mt-3 text-sm">GMARI™ {draft.gmari.score}/100 — {draft.gmari.band}</p>
        </div>
        <div className="px-10 py-8 space-y-8">
          {draft.sections.map((s) => {
            const m = STATUS_META[s.status] || STATUS_META.drafted;
            return (
              <section key={s.id} data-testid={`draft-section-${s.id}`}>
                <div className="flex items-baseline justify-between border-b border-slate-200 pb-1.5 mb-3">
                  <h3 className="font-heading font-bold text-sm" style={{ color: "#0F2A3D" }}>{s.title}</h3>
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white ${m.cls}`}>{m.label}</span>
                </div>
                <div className="space-y-3">
                  {s.blocks.map((b, i) => <Block key={i} b={b} />)}
                </div>
                <p className="font-mono-data text-[10px] text-slate-400 mt-3">Sources: {s.provenance.join(" · ")}</p>
              </section>
            );
          })}
        </div>
      </div>
    </>
  );
}

function AssessTab({ productId }) {
  const [docs, setDocs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/ingestion/documents`, { params: { product_id: productId, doc_type: "gvd" } }).then((r) => setDocs(r.data));
    axios.get(`${API}/gvd/${productId}`).then((r) => setAssessments(r.data));
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const processing = assessments.some((a) => a.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [processing, load]);

  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("product_id", productId);
    form.append("doc_type", "gvd");
    setUploading(true);
    try {
      await axios.post(`${API}/ingestion/upload`, form);
      toast.success("GVD uploaded — run assessment when ready");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const assess = async (docId) => {
    try {
      await axios.post(`${API}/gvd/${productId}/assess`, { document_id: docId });
      toast.success("Assessment started — GPT-5.5 is auditing your GVD against country expectations");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start assessment");
    }
  };

  const latest = assessments.find((a) => a.status === "completed");
  const r = latest?.result;

  return (
    <>
      <section data-testid="gvd-documents" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-sm tracking-tight">Uploaded Dossiers</p>
            <p className="text-xs text-slate-500 mt-1">The engine compares your GVD against evidence expectations of every target market and produces a structured enhancement plan.</p>
          </div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <button data-testid="upload-gvd-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50 shrink-0">
            {uploading ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
            Upload GVD
          </button>
        </div>
        {docs.length === 0 ? (
          <p className="px-5 py-10 text-center text-xs text-slate-400">No GVD uploaded yet.</p>
        ) : docs.map((d) => {
          const running = assessments.find((a) => a.document_id === d.id && a.status === "processing");
          return (
            <div key={d.id} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-center gap-4">
              <FileText size={18} className="text-slate-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{d.filename}</p>
                <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">{new Date(d.created_at).toLocaleString()} · {Math.round(d.chars / 1000)}k chars</p>
              </div>
              <button data-testid={`assess-gvd-${d.id}`} disabled={!!running} onClick={() => assess(d.id)}
                className="text-xs font-semibold text-slate-900 border border-slate-300 px-3 py-2 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
                {running ? <><CircleNotch size={12} className="animate-spin" /> Assessing…</> : <><Sparkle size={12} weight="bold" /> Run Assessment</>}
              </button>
            </div>
          );
        })}
      </section>

      {assessments.some((a) => a.status === "failed") && !latest && (
        <p className="text-xs text-red-600">Last assessment failed — please retry.</p>
      )}

      {r && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div data-testid="gvd-score-card" className="bg-slate-900 text-white p-8">
              <p className="overline-label text-white/50">GVD Alignment Score · {latest.filename} <ScoreInfo scoreId="gvd_alignment" light /></p>
              <p className="font-heading font-extrabold leading-none mt-4" style={{ fontSize: "72px" }}>
                {r.overall_alignment_score}<span className="text-2xl text-white/40">/100</span>
              </p>
              <p className="text-xs text-white/60 leading-relaxed mt-6">{r.verdict}</p>
              <div className="mt-6 space-y-1.5">
                {(r.strengths || []).map((s, i) => (
                  <p key={i} className="text-[11px] text-emerald-300/90">✓ {s}</p>
                ))}
              </div>
            </div>
            <div data-testid="gvd-section-coverage" className="xl:col-span-2 bg-white border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-200">
                <p className="font-heading font-bold text-sm tracking-tight">Section Coverage</p>
              </div>
              {(r.section_coverage || []).map((s, i) => (
                <div key={i} className="px-5 py-2.5 border-b border-slate-100 last:border-0 flex items-start gap-3">
                  <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white w-16 text-center shrink-0 mt-0.5" style={{ background: SECTION_COLOR[s.status] || "#64748B" }}>{s.status}</span>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{s.section}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{s.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <section data-testid="gvd-missing-narratives" className="bg-white border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-200">
                <p className="font-heading font-bold text-sm tracking-tight">Missing Evidence Narratives</p>
              </div>
              {(r.missing_narratives || []).map((m, i) => (
                <div key={i} className="px-5 py-3 border-b border-slate-100 last:border-0">
                  <p className="text-xs font-semibold text-slate-800">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white mr-2" style={{ background: PRIORITY_COLOR[m.priority] || "#64748B" }}>{m.priority}</span>
                    {m.title}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{m.rationale}</p>
                  {m.markets?.length > 0 && <p className="font-mono-data text-[10px] text-slate-400 mt-1">Affects: {m.markets.join(", ")}</p>}
                </div>
              ))}
            </section>
            <section data-testid="gvd-local-adaptations" className="bg-white border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-200">
                <p className="font-heading font-bold text-sm tracking-tight">Local Adaptation Needs</p>
              </div>
              {(r.local_adaptations || []).map((a, i) => (
                <div key={i} className="px-5 py-3 border-b border-slate-100 last:border-0">
                  <p className="text-xs font-semibold text-slate-800">{a.market}<span className="ml-2 font-normal text-slate-400">{a.requirement_type}</span></p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{a.adaptation}</p>
                </div>
              ))}
            </section>
          </div>

          <section data-testid="gvd-enhancement-plan" className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <p className="font-heading font-bold text-sm tracking-tight">Enhancement Plan</p>
              <p className="text-xs text-slate-500 mt-1">Ordered by impact on market access readiness.</p>
            </div>
            {(r.enhancement_plan || []).map((p, i) => (
              <div key={i} className="px-5 py-3 border-b border-slate-100 last:border-0 flex items-start gap-4">
                <span className="font-mono-data text-[10px] text-slate-300 mt-0.5 shrink-0">{String(p.step || i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-800">{p.action}
                    <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-200 text-slate-400">{p.effort} effort</span>
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{p.impact}</p>
                </div>
              </div>
            ))}
          </section>
        </>
      )}
      {!r && docs.length > 0 && !processing && (
        <p className="text-center text-xs text-slate-400 py-8">Run an assessment to see section coverage, missing narratives and the enhancement plan.</p>
      )}
    </>
  );
}

export default function GVDPage() {
  const { productId, product } = useProduct();
  const [tab, setTab] = useState("draft");

  return (
    <PageShell pageId="gvd" overline={`GVD Engine · ${product?.name || ""}`} title="Global Value Dossier">
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["draft", "Auto-Draft"], ["assess", "Assess Uploaded GVD"]].map(([id, label]) => (
          <button key={id} data-testid={`gvd-tab-${id}`} onClick={() => setTab(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "draft" ? <DraftTab productId={productId} product={product} /> : <AssessTab productId={productId} />}
    </PageShell>
  );
}
